import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { ArrowRight, Clock, ChevronLeft, BookOpen } from 'lucide-react';
import { api } from '../lib/api';
import { useSEO, buildArticleSchema } from '../lib/seo';

const PUBLIC_BASE = process.env.REACT_APP_BACKEND_URL || '';

export function BlogListPage() {
  const [posts, setPosts] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  useSEO({
    title: 'Blog — ValutDocu | PDF Tips, Guides & Tutorials',
    description: 'Free guides on PDF tools, iLovePDF alternatives, compression, conversion, and AI-powered document workflows.',
    keywords: 'pdf blog, pdf tutorials, ilovepdf alternative, merge pdf guide, compress pdf',
    canonical: `${PUBLIC_BASE}/blog`,
  });

  React.useEffect(() => {
    api.get('/blog/posts').then(({ data }) => setPosts(data.posts)).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-list-page" className="vault-grid-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
        <div className="mb-10">
          <p className="text-xs tracking-[0.2em] uppercase font-mono text-vault-gold mb-3">Vault Journal</p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold tracking-tighter">
            PDF tips, <span className="gold-text">straight from the vault.</span>
          </h1>
          <p className="text-gray-400 mt-4 max-w-2xl">
            Guides, comparisons, and workflows from the team building ValutDocu.
            No fluff — just the fastest path to getting things done.
          </p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="tool-card rounded-2xl p-6 animate-pulse">
                <div className="h-6 w-2/3 bg-white/10 rounded mb-4" />
                <div className="h-3 w-full bg-white/10 rounded mb-2" />
                <div className="h-3 w-5/6 bg-white/10 rounded" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {posts.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="tool-card rounded-2xl p-6 group block"
              >
                <div className="text-4xl mb-4">{p.cover_emoji || '📄'}</div>
                <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
                  <Clock className="w-3 h-3" />
                  <span>{p.read_minutes || 5} min read</span>
                  {p.tags?.slice(0, 2).map((t) => (
                    <span key={t} className="text-vault-gold/70 font-mono uppercase tracking-wider text-[10px]">· {t}</span>
                  ))}
                </div>
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-white mb-2 group-hover:text-vault-gold transition leading-tight">
                  {p.title}
                </h2>
                <p className="text-sm text-gray-400 leading-relaxed">{p.excerpt}</p>
                <div className="mt-4 text-xs font-mono tracking-wider text-vault-gold/70 inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
                  READ ARTICLE <ArrowRight className="w-3 h-3" />
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    api.get(`/blog/posts/${slug}`).then(({ data }) => setPost(data))
      .catch((e) => { if (e.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  useSEO({
    title: post ? `${post.title} — ValutDocu Blog` : 'Loading… — ValutDocu',
    description: post?.excerpt || '',
    keywords: post?.tags?.join(', '),
    canonical: post ? `${PUBLIC_BASE}/blog/${post.slug}` : undefined,
    schemas: post ? [buildArticleSchema(post, PUBLIC_BASE)] : [],
  });

  if (notFound) return <Navigate to="/blog" replace />;
  if (loading || !post) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center text-gray-400">
        <BookOpen className="w-5 h-5 mr-2 animate-pulse" /> Loading article…
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page" className="vault-grid-bg min-h-[calc(100vh-4rem)]">
      <article className="max-w-3xl mx-auto px-6 lg:px-8 py-12">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-vault-gold mb-8 transition">
          <ChevronLeft className="w-4 h-4" /> All articles
        </Link>

        <div className="text-5xl mb-6">{post.cover_emoji || '📄'}</div>
        <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" /> {post.read_minutes || 5} min read
          </span>
          {post.tags?.map((t) => (
            <span key={t} className="px-2 py-0.5 rounded-full bg-vault-gold/10 border border-vault-gold/30 text-vault-gold font-mono tracking-wider text-[10px] uppercase">
              {t}
            </span>
          ))}
        </div>

        <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold tracking-tighter mb-4">
          {post.title}
        </h1>
        <p className="text-lg text-gray-300 leading-relaxed mb-10">{post.excerpt}</p>

        <div data-testid="blog-content" className="prose prose-invert prose-headings:font-heading prose-headings:tracking-tight prose-headings:text-white prose-h2:text-vault-gold prose-h2:text-2xl prose-a:text-vault-gold prose-a:no-underline hover:prose-a:underline prose-strong:text-white prose-blockquote:border-l-vault-gold prose-blockquote:text-gray-300 prose-table:text-sm prose-th:text-vault-gold prose-th:border-white/20 prose-td:border-white/10 max-w-none text-gray-300">
          <ReactMarkdown>{post.content}</ReactMarkdown>
        </div>

        <div className="mt-16 pt-10 border-t border-white/10 text-center">
          <h3 className="text-2xl font-heading font-bold tracking-tight mb-3">Ready to try ValutDocu?</h3>
          <p className="text-gray-400 mb-6">Free forever. 37 tools. No watermarks. No signup.</p>
          <Link to="/" className="btn-primary inline-flex items-center gap-2">Explore all tools</Link>
        </div>
      </article>
    </div>
  );
}
